import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Building2,
  Inbox,
  Plus,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useBiens } from "@/hooks/useBiens";
import { useRole } from "@/contexts/RoleContext";
import { getV2NavForRole } from "@/v2/lib/v2Roles";

interface CommandItem {
  id: string;
  label: string;
  hint?: string;
  icon: typeof Building2;
  action: () => void;
  category: "navigation" | "biens" | "actions";
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { role } = useRole();
  const { data: biens } = useBiens();
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus auto à l'ouverture
  useEffect(() => {
    if (open) {
      setQuery("");
      setSelected(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Construction des items
  const items = useMemo<CommandItem[]>(() => {
    const navItems = getV2NavForRole(role).map((n) => ({
      id: `nav:${n.to}`,
      label: t(n.labelKey),
      icon: n.icon,
      action: () => {
        navigate(n.to);
        onClose();
      },
      category: "navigation" as const,
    }));

    const actions: CommandItem[] = [];
    if (role === "admin" || role === "c_level" || role === "collaborateur") {
      actions.push({
        id: "new-bien",
        label: t("v2.commandPalette.newBien"),
        icon: Plus,
        action: () => {
          navigate("/v2/biens/new");
          onClose();
        },
        category: "actions",
      });
    }

    const bienItems: CommandItem[] = (biens || []).map((b) => ({
      id: `bien:${b.id}`,
      label: `${b.referenceInterne || b.reference || b.id || "—"} — ${b.adresse || "—"}`,
      hint: [b.ville, b.disponibilite].filter(Boolean).join(" · "),
      icon: Building2,
      action: () => {
        navigate(`/v2/biens/${b.id}`);
        onClose();
      },
      category: "biens",
    }));

    return [...actions, ...navItems, ...bienItems];
  }, [role, biens, navigate, onClose, t]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const match = (i: CommandItem) =>
      !q ||
      i.label.toLowerCase().includes(q) ||
      (i.hint || "").toLowerCase().includes(q);
    // Cherche sur TOUS les biens (~1559) puis plafonne à 50 RÉSULTATS pour
    // garder le rendu fluide. Avant, le slice(0,50) s'appliquait AVANT le
    // filtre → seuls les 50 premiers biens étaient cherchables (bug : un bien
    // comme "AMP_AA2.1" restait introuvable alors qu'il existe).
    // Ordre [actions, navigation, biens] préservé → cohérent avec le rendu
    // groupé et la navigation clavier (filtered[selected]).
    const nonBiens = items.filter((i) => i.category !== "biens" && match(i));
    const bienResults = items
      .filter((i) => i.category === "biens" && match(i))
      .slice(0, 50);
    return [...nonBiens, ...bienResults];
  }, [items, query]);

  useEffect(() => {
    setSelected(0);
  }, [query]);

  // Navigation clavier
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelected((s) => Math.min(s + 1, filtered.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelected((s) => Math.max(s - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        filtered[selected]?.action();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, filtered, selected]);

  if (!open) return null;

  const grouped: Record<string, CommandItem[]> = {};
  filtered.forEach((i) => {
    if (!grouped[i.category]) grouped[i.category] = [];
    grouped[i.category].push(i);
  });

  const categoryLabels: Record<string, string> = {
    navigation: t("v2.commandPalette.categoryNavigation"),
    biens: t("v2.commandPalette.categoryBiens"),
    actions: t("common.actions"),
  };

  let counter = 0;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center pt-[10vh] px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl bg-surface rounded-lg border border-border shadow-2xl overflow-hidden flex flex-col max-h-[70vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-border">
          <Search size={16} className="text-text-muted" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("v2.commandPalette.placeholder")}
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-text-muted"
          />
          <kbd className="text-[10px] text-text-muted bg-background border border-border rounded px-1.5 py-0.5">
            ESC
          </kbd>
        </div>

        <div className="flex-1 overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <div className="text-center text-text-muted text-sm py-8">
              {t("v2.commandPalette.noMatch")}
            </div>
          ) : (
            ["actions", "navigation", "biens"].map((cat) => {
              const list = grouped[cat];
              if (!list || list.length === 0) return null;
              return (
                <div key={cat}>
                  <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-text-muted font-semibold">
                    {categoryLabels[cat]}
                  </div>
                  {list.map((item) => {
                    const idx = counter++;
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => item.action()}
                        onMouseEnter={() => setSelected(idx)}
                        className={cn(
                          "w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left cursor-pointer",
                          idx === selected
                            ? "bg-accent-light text-accent"
                            : "text-text-primary hover:bg-gray-50",
                        )}
                      >
                        <Icon
                          size={14}
                          className={
                            idx === selected ? "text-accent" : "text-text-muted"
                          }
                        />
                        <span className="flex-1 truncate">{item.label}</span>
                        {item.hint && (
                          <span className="text-xs text-text-muted truncate max-w-[40%]">
                            {item.hint}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>

        <div className="border-t border-border px-3 py-1.5 text-[10px] text-text-muted flex items-center gap-3">
          <span>
            <kbd className="bg-background border border-border rounded px-1">
              ↑↓
            </kbd>{" "}
            {t("v2.commandPalette.navigate")}
          </span>
          <span>
            <kbd className="bg-background border border-border rounded px-1">
              ↵
            </kbd>{" "}
            {t("v2.commandPalette.open")}
          </span>
          <span className="ml-auto inline-flex items-center gap-1">
            <Inbox size={10} />{" "}
            {t("v2.commandPalette.indexed", { count: (biens || []).length })}
          </span>
        </div>
      </div>
    </div>
  );
}
